import { useCallback, useEffect, useMemo, useState } from 'react';
import { SecondaryBase } from './../Commons/SecondaryBase';
import { Confirm } from './../../Commons/Modals/Confirm';
import { MESSAGE_TYPE, useFloatMessage } from './../../Providers/FloatMessage';
import {
    GetAnecdotesPage as BLGetAnecdotesPage,
    GetAllAnecdotes as BLGetAllAnecdotes,
    GetNewerAnecdotes as BLGetNewerAnecdotes,
    ToggleAnecdoteVisibility as BLToggleVisibility,
    ToggleAnecdoteShown as BLToggleShown,
    DeleteAnecdote as BLDeleteAnecdote,
    ResetAnecdotes as BLResetAnecdotes,
    SelectAnecdote as BLSelectAnecdote,
    DeselectAnecdote as BLDeselectAnecdote,
    SetAnecdoteMode as BLSetAnecdoteMode,
    SetAnecdotePosition as BLSetAnecdotePosition,
    DownloadExcelReport as BLDownloadExcelReport,
    formatFullDate
} from './../../../utilities/BL/Admin/Overlays/Anecdotes';
import { UseAnecdotePlayer, INTERVAL_PRESETS } from './Anecdotes/UseAnecdotePlayer';

// ==============================================================================
// CONFIGURACIÓN
// ==============================================================================
const PAGE_SIZE = 30;

const MODAL_TYPES = {
    DELETE: 'DELETE',
    RESET: 'RESET',
    PROJECT: 'PROJECT',
    CLEAR_SCREEN: 'CLEAR_SCREEN',
    CLEAR_QUEUE: 'CLEAR_QUEUE',
    TOGGLE_LIVE: 'TOGGLE_LIVE'
};

const Anecdotes = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();
    const [anecdotes, setAnecdotes] = useState([]);
    const [totalCount, setTotalCount] = useState(0);

    // Modo de pantalla en vivo: 'fullscreen' o 'cintillo'
    const [displayMode, setDisplayMode] = useState('fullscreen');
    // Posición vertical del cintillo (offset Y)
    const [verticalPos, setVerticalPos] = useState(0);

    // Paginación y carga
    const [nextCursor, setNextCursor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sortOrder, setSortOrder] = useState('DESC');

    // Modales de confirmación
    const [showModal, setShowModal] = useState(false);
    const [modalConfig, setModalConfig] = useState({ type: '', data: null });

    // ----------------------------------------------------------------------------
    // 1. INICIALIZACIÓN DEL REPRODUCTOR
    // ----------------------------------------------------------------------------
    const playerActions = useMemo(() => ({
        sendToScreen: async (item, mode) => {
            const currentMode = mode || displayMode;
            const resp = await BLSelectAnecdote(item.id, currentMode);
            if (!resp.success) throw new Error(resp.message);
            setAnecdotes(prev => prev.map(a => a.id === item.id ? { ...a, wasShown: true, isSelected: true } : { ...a, isSelected: false }));
        },
        clearScreen: async () => {
            await BLDeselectAnecdote();
            setAnecdotes(prev => prev.map(a => ({ ...a, isSelected: false })));
        }
    }), [displayMode]);

    const player = UseAnecdotePlayer(playerActions);
    const currentQItem = player.queue[player.currentIndex];

    // ----------------------------------------------------------------------------
    // 2. CARGA DE DATOS
    // ----------------------------------------------------------------------------
    const loadInitialData = async () => {
        setLoading(true);
        try {
            const resp = await BLGetAnecdotesPage({ limit: PAGE_SIZE, cursor: null, sort: sortOrder });
            if (!resp.success) throw new Error(resp.message);
            setAnecdotes(resp.data.items || []);
            setNextCursor(resp.data.nextCursor);
            setTotalCount(resp.data.total || (resp.data.items || []).length);
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Anécdotas actualizadas.' });
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: error.message || 'Error al obtener anécdotas.' });
        } finally {
            setLoading(false);
        }
    };

    const loadMoreHistory = async () => {
        if (!nextCursor) {
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'No hay más anécdotas por cargar.' });
            return;
        }
        if (loading) return;

        setLoading(true);
        try {
            const resp = await BLGetAnecdotesPage({ limit: PAGE_SIZE, cursor: nextCursor, sort: sortOrder });
            if (!resp.success) throw new Error(resp.message);
            const newItems = resp.data.items || [];
            setAnecdotes(prev => [...prev, ...newItems]);
            setNextCursor(resp.data.nextCursor);
            if (newItems.length > 0) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Se cargaron ${newItems.length} anécdota(s) más.` });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al cargar más historial.' });
        } finally {
            setLoading(false);
        }
    };

    const checkNewAnecdotes = async () => {
        if (loading) return;
        setLoading(true);
        try {
            const maxId = anecdotes.length > 0 ? Math.max(...anecdotes.map(a => a.id)) : 0;
            const resp = await BLGetNewerAnecdotes(maxId);
            if (!resp.success) throw new Error(resp.message);

            const newItems = resp.data || [];
            if (newItems.length > 0) {
                setAnecdotes(prev => [...newItems, ...prev]);
                setTotalCount(prev => prev + newItems.length);
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Se encontraron ${newItems.length} anécdota(s) nueva(s).` });
            } else {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'No hay anécdotas nuevas por el momento.' });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al buscar nuevas anécdotas.' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadInitialData();
    }, [sortOrder]);

    // ----------------------------------------------------------------------------
    // 3. CAMBIO DE MODO Y POSICIÓN
    // ----------------------------------------------------------------------------
    const handleModeSwitch = async (newMode) => {
        setDisplayMode(newMode);
        player.setDisplayMode(newMode);
        try {
            await BLSetAnecdoteMode(newMode);
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Modo de pantalla: ${newMode === 'cintillo' ? 'Cintillo' : 'Pantalla Completa'}` });
        } catch (error) {
            console.error("Error al emitir set-mode:", error);
        }
    };

    const handleSliderChange = (e) => {
        const val = Number(e.target.value);
        setVerticalPos(val);
        BLSetAnecdotePosition(val);
    };

    const handleInputChange = (e) => {
        setVerticalPos(Number(e.target.value) || 0);
    };

    const applyInputPosition = () => {
        BLSetAnecdotePosition(verticalPos);
        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Posición aplicada: ${verticalPos}px` });
    };

    // ----------------------------------------------------------------------------
    // 4. ACCIONES SOBRE ANÉCDOTAS (MODERACIÓN, VISTO, BORRADO)
    // ----------------------------------------------------------------------------
    const toggleHide = async (item) => {
        try {
            const resp = await BLToggleVisibility(item.id);
            if (resp.success) {
                setAnecdotes(prev => prev.map(a => a.id === item.id ? { ...a, isHidden: !a.isHidden } : a));
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: item.isHidden ? 'Anécdota visible' : 'Anécdota oculta' });
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al cambiar visibilidad' });
        }
    };

    const toggleShown = async (item) => {
        try {
            const resp = await BLToggleShown(item.id);
            if (resp.success) {
                setAnecdotes(prev => prev.map(a => a.id === item.id ? { ...a, wasShown: !a.wasShown } : a));
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: item.wasShown ? 'Marcada como no vista' : 'Marcada como vista' });
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al cambiar estado visto' });
        }
    };

    const executeDelete = async () => {
        const id = modalConfig.data?.id;
        if (!id) return;
        try {
            const resp = await BLDeleteAnecdote(id);
            if (resp.success) {
                setAnecdotes(prev => prev.filter(a => a.id !== id));
                setTotalCount(c => Math.max(0, c - 1));
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Anécdota eliminada' });
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al eliminar anécdota' });
        } finally {
            setShowModal(false);
        }
    };

    const executeReset = async () => {
        try {
            const resp = await BLResetAnecdotes();
            if (resp.success) {
                setAnecdotes([]);
                setTotalCount(0);
                setNextCursor(null);
                player.clearQueue();
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Todas las anécdotas fueron eliminadas' });
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al reiniciar dinámica' });
        } finally {
            setShowModal(false);
        }
    };

    const executeProject = async () => {
        const item = modalConfig.data;
        if (!item) return;
        try {
            const resp = await BLSelectAnecdote(item.id, displayMode);
            if (resp.success) {
                setAnecdotes(prev => prev.map(a => a.id === item.id ? { ...a, wasShown: true, isSelected: true } : { ...a, isSelected: false }));
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Anécdota proyectada en pantalla' });
            }
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al proyectar anécdota' });
        } finally {
            setShowModal(false);
        }
    };

    const executeClearScreen = async () => {
        try {
            await BLDeselectAnecdote();
            setAnecdotes(prev => prev.map(a => ({ ...a, isSelected: false })));
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Pantalla limpia' });
        } catch {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al limpiar pantalla' });
        } finally {
            setShowModal(false);
        }
    };

    const executeClearQueue = () => {
        player.clearQueue();
        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Fila de reproducción vaciada' });
        setShowModal(false);
    };

    const executeToggleLive = async () => {
        await player.toggleLive();
        setShowModal(false);
    };

    // ----------------------------------------------------------------------------
    // 5. MODAL TRIGGERS
    // ----------------------------------------------------------------------------
    const requestDelete = (item) => {
        setModalConfig({ type: MODAL_TYPES.DELETE, data: item });
        setShowModal(true);
    };

    const requestReset = () => {
        setModalConfig({ type: MODAL_TYPES.RESET, data: null });
        setShowModal(true);
    };

    const requestProject = (item) => {
        setModalConfig({ type: MODAL_TYPES.PROJECT, data: item });
        setShowModal(true);
    };

    const requestClearScreen = () => {
        setModalConfig({ type: MODAL_TYPES.CLEAR_SCREEN, data: null });
        setShowModal(true);
    };

    const requestClearQueue = () => {
        setModalConfig({ type: MODAL_TYPES.CLEAR_QUEUE, data: null });
        setShowModal(true);
    };

    const requestToggleLive = () => {
        setModalConfig({ type: MODAL_TYPES.TOGGLE_LIVE, data: { isGoingLive: !player.isLive } });
        setShowModal(true);
    };

    // ----------------------------------------------------------------------------
    // 6. EXPORTAR REPORTE EXCEL
    // ----------------------------------------------------------------------------
    const handleExportExcel = async () => {
        setLoading(true);
        try {
            const resp = await BLGetAllAnecdotes();
            if (!resp.success) throw new Error(resp.message);
            const res = BLDownloadExcelReport(resp.data);
            if (res.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Reporte descargado: ${res.fileName}` });
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: error.message || 'Error al exportar reporte.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <SecondaryBase getOutOfTheDynamic={getOutOfTheDynamic}>
            <p className='Admin__Anecdotes__Text--Title text-center'>
                <i className='bi bi-chat-quote-fill me-2 text-danger'></i> Tu Mejor Anécdota
            </p>

            {/* BARRA SUPERIOR CON CONTROLES RÁPIDOS */}
            <div className='d-flex flex-wrap justify-content-center align-items-center mt-3 gap-3'>
                <button
                    type="button"
                    className='Btn__Adsl--RedOutlineTransparent mb-0'
                    onClick={requestClearScreen}
                    title="Limpiar Pantalla"
                >
                    <i className='bi bi-x-square me-1'></i> Quitar anécdota de la pantalla
                </button>
                <button
                    type="button"
                    className='Btn__Adsl--RedOutlineTransparent mb-0'
                    onClick={handleExportExcel}
                    disabled={loading}
                    title="Exportar a Excel"
                >
                    <i className='bi bi-file-earmark-excel-fill text-success me-1'></i> Exportar anécdotas a Excel
                </button>
            </div>

            {/* SWITCH EN VIVO DE MODO: CINTILLO VS PANTALLA COMPLETA */}
            <div className='Admin__Anecdotes__ModeSwitchContainer mt-4 p-3 rounded bg-dark border border-secondary mx-auto text-center' style={{ maxWidth: '650px' }}>
                <p className='text-white mb-2 fw-bold'>Modo de Proyección en Overlay:</p>
                <div className='btn-group w-100' role="group">
                    <button
                        type="button"
                        className={`btn ${displayMode === 'cintillo' ? 'btn-danger fw-bold shadow' : 'btn-outline-secondary text-white'}`}
                        onClick={() => handleModeSwitch('cintillo')}
                    >
                        <i className='bi bi-layout-text-window-reverse me-1'></i> 📏 Cintillo (Lower Third)
                    </button>
                    <button
                        type="button"
                        className={`btn ${displayMode === 'fullscreen' ? 'btn-danger fw-bold shadow' : 'btn-outline-secondary text-white'}`}
                        onClick={() => handleModeSwitch('fullscreen')}
                    >
                        <i className='bi bi-aspect-ratio me-1'></i> 📺 Pantalla Completa
                    </button>
                </div>
            </div>

            {/* SLIDER DE POSICIÓN Y (VISIBLE PARA MODO CINTILLO) */}
            {displayMode === 'cintillo' && (
                <div className='d-flex flex-column align-items-center mt-4 p-3 bg-dark border border-secondary rounded mx-auto' style={{ maxWidth: '400px' }}>
                    <p className='mb-2 text-center text-white fw-bold'>Posición 'Y' del Cintillo en Pantalla:</p>
                    <input
                        type="range"
                        className="d-block form-range w-100"
                        min="0"
                        max="700"
                        step="5"
                        value={verticalPos}
                        onChange={handleSliderChange}
                    />
                    <div className='d-flex align-items-center gap-2 mt-2 w-100'>
                        <input
                            type="number"
                            className="form-control form-control-sm text-center border-secondary bg-dark text-white"
                            value={verticalPos}
                            onChange={handleInputChange}
                        />
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={applyInputPosition}
                        >
                            Aplicar
                        </button>
                    </div>
                </div>
            )}

            {/* REPRODUCTOR EN COLA */}
            <div className='Admin__Anecdotes__PlayerContainer'>
                <div className='Admin__Anecdotes__Player'>
                    {/* DECK DE TRANSMISIÓN */}
                    <div className='Admin__Anecdotes__Player__Deck'>
                        <div className='d-flex justify-content-between align-items-center mb-2'>
                            <span className={`badge ${player.isLive ? 'bg-success' : 'bg-danger'}`}>
                                {player.isLive ? 'En vivo' : 'Fuera del aire'}
                            </span>
                            <span className='small text-truncate' style={{ maxWidth: '250px' }}>
                                {player.projectionStatus}
                            </span>
                        </div>

                        {/* SELECTOR DE INTERVALO & BOTÓN GO LIVE */}
                        <div className='Admin__Anecdotes__Player__Deck__Controls mt-3'>
                            <div className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
                                <div className='d-flex align-items-center gap-2'>
                                    <small>Intervalo:</small>
                                    <select
                                        className='form-select form-select-sm w-auto bg-dark text-white border-secondary'
                                        value={player.autoPlayInterval}
                                        onChange={(e) => player.setAutoPlayInterval(Number(e.target.value))}
                                    >
                                        {INTERVAL_PRESETS.map(p => (
                                            <option key={p.value} value={p.value}>{p.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <button
                                    type='button'
                                    className={`btn btn-sm ${player.isLive ? 'btn-outline-danger' : 'btn-outline-success'}`}
                                    onClick={requestToggleLive}
                                >
                                    <i className='bi bi-broadcast me-1'></i>
                                    {player.isLive ? 'Detener proyección' : 'Iniciar proyección'}
                                </button>
                            </div>
                        </div>

                        {/* PREVIEW EN TIEMPO REAL DEL DECK */}
                        <div className='Admin__Anecdotes__Player__Deck__Screen mt-3'>
                            {currentQItem ? (
                                <div className='Admin__Anecdotes__Player__Deck__Screen__Content p-3'>
                                    <p className="msg-body fs-5 fw-bold mb-2">"{currentQItem.content}"</p>
                                    <p className="msg-sender text-danger mb-0">- {currentQItem.sender || 'Anónimo'}</p>
                                </div>
                            ) : (
                                <div className='Admin__Anecdotes__Player__Deck__Screen__Empty text-muted text-center py-4'>
                                    Fila vacía
                                </div>
                            )}
                        </div>

                        {/* BOTONES DE CONTROL DE PLAY/PAUSE/PREV/NEXT */}
                        <div className='Admin__Anecdotes__Player__Deck__Controls mt-3'>
                            <div className='d-flex justify-content-center gap-2 mb-2'>
                                <button
                                    type='button'
                                    className='Btn__Adsl--RedOutlineTransparent mb-0'
                                    onClick={player.playPrev}
                                    title="Anterior"
                                >
                                    <i className='bi bi-skip-backward-fill'></i>
                                </button>
                                <button
                                    type='button'
                                    className={`Btn__Adsl--${player.isPlaying ? 'Red' : 'RedOutlineTransparent'} mb-0`}
                                    onClick={player.togglePlay}
                                    title={player.isPlaying ? "Pausar" : "Reproducir"}
                                >
                                    <i className={`bi bi-${player.isPlaying ? 'pause-fill' : 'play-fill'}`}></i>
                                </button>
                                <button
                                    type='button'
                                    className='Btn__Adsl--RedOutlineTransparent mb-0'
                                    onClick={player.playNext}
                                    title="Siguiente"
                                >
                                    <i className='bi bi-skip-forward-fill'></i>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* LISTA / PLAYLIST DE LA FILA */}
                    <div className='Admin__Anecdotes__Player__Playlist'>
                        <div className='d-flex justify-content-between align-items-center mb-2'>
                            <span className='fw-bold'>Fila ({player.queue.length})</span>
                            <button
                                type='button'
                                className='btn btn-link btn-sm text-danger text-decoration-none'
                                onClick={requestClearQueue}
                                disabled={player.queue.length === 0}
                            >
                                Vaciar
                            </button>
                        </div>
                        <div className='Admin__Anecdotes__Player__Playlist__List'>
                            {player.queue.length === 0 ? (
                                <p className='text-center small mt-4 text-muted'>Agrega anécdotas desde la biblioteca inferior</p>
                            ) : (
                                player.queue.map((item, idx) => (
                                    <div
                                        key={item._qid}
                                        className={`Admin__Anecdotes__Player__Playlist__Item ${idx === player.currentIndex ? 'active' : ''}`}
                                        onClick={() => player.jumpTo(idx)}
                                    >
                                        <button
                                            type='button'
                                            className='btn btn-sm text-danger p-0 me-2'
                                            onClick={(e) => { e.stopPropagation(); player.removeFromQueue(idx); }}
                                            title="Quitar de la fila"
                                        >
                                            <i className='bi bi-trash'></i>
                                        </button>
                                        <div className="text-truncate flex-grow-1">
                                            <small className="fw-bold d-block text-white">#{item.id} - {item.sender}</small>
                                            <small className="text-truncate d-block text-muted">{item.content}</small>
                                        </div>
                                        <div className='d-flex flex-column ms-2'>
                                            <button
                                                type='button'
                                                className='btn btn-sm p-0 text-white'
                                                onClick={(e) => { e.stopPropagation(); player.reorderQueue(idx, idx - 1); }}
                                                title="Subir"
                                            >
                                                <i className='bi bi-chevron-up'></i>
                                            </button>
                                            <button
                                                type='button'
                                                className='btn btn-sm p-0 text-white'
                                                onClick={(e) => { e.stopPropagation(); player.reorderQueue(idx, idx + 1); }}
                                                title="Bajar"
                                            >
                                                <i className='bi bi-chevron-down'></i>
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* BIBLIOTECA DE ANÉCDOTAS */}
            <div className='border border-secondary rounded pt-4 pb-4 mt-5 px-3'>
                <p className='text-center mb-1 fs-5 fw-bold text-white'>Biblioteca de Anécdotas</p>
                <p className='text-center mb-0 text-muted'>Total registradas: {totalCount} | Mostrando: {anecdotes.length}</p>

                <div className='d-flex justify-content-center px-4 mt-3 mb-4'>
                    <button
                        type="button"
                        className="Btn__Adsl--RedOutlineTransparent"
                        onClick={checkNewAnecdotes}
                        disabled={loading}
                    >
                        <i className='bi bi-arrow-clockwise me-1'></i> Buscar nuevas anécdotas
                    </button>
                </div>

                <section className='Admin__Anecdotes__Library'>
                    {anecdotes.length > 0 ? anecdotes.map((item) => (
                        <div
                            key={item.id}
                            className={`Admin__Anecdotes__Library__Message ${item.isHidden ? 'opacity-50' : ''} ${item.isSelected ? 'Admin__Anecdotes__Library__Message--Active' : ''}`}
                            style={item.wasShown ? { border: '2px solid #28a745' } : {}}
                        >
                            {/* Botones de acción rápida en tarjeta */}
                            <div className='Admin__Anecdotes__Library__Message__ActionButtons'>
                                <button
                                    type='button'
                                    className='Btn__Adsl--RedOutlineTransparent Admin__Anecdotes__Library__Message__ActionButtons__Button'
                                    disabled={item.isHidden}
                                    onClick={() => player.addToQueue(item)}
                                    title="Agregar a fila"
                                >
                                    <i className='bi bi-plus-lg'></i>
                                </button>
                                <button
                                    type='button'
                                    className={`Btn__Adsl--RedOutlineTransparent Admin__Anecdotes__Library__Message__ActionButtons__Button ${item.isHidden ? 'text-warning' : ''}`}
                                    onClick={() => toggleHide(item)}
                                    title={item.isHidden ? "Mostrar anécdota" : "Ocultar / Moderar"}
                                >
                                    <i className={`bi ${item.isHidden ? 'bi-eye-slash-fill' : 'bi-eye'}`}></i>
                                </button>
                                <button
                                    type='button'
                                    className={`Btn__Adsl--RedOutlineTransparent Admin__Anecdotes__Library__Message__ActionButtons__Button ${item.wasShown ? 'text-success' : ''}`}
                                    onClick={() => toggleShown(item)}
                                    title={item.wasShown ? "Marcar como no vista" : "Marcar como vista"}
                                >
                                    <i className={`bi ${item.wasShown ? 'bi-check-circle-fill' : 'bi-circle'}`}></i>
                                </button>
                                <button
                                    type='button'
                                    className='Btn__Adsl--RedOutlineTransparent Admin__Anecdotes__Library__Message__ActionButtons__Button'
                                    onClick={() => requestDelete(item)}
                                    title="Eliminar anécdota"
                                >
                                    <i className='bi bi-trash'></i>
                                </button>
                            </div>

                            {/* Contenido de la anécdota */}
                            <div className="flex-grow-1 d-flex flex-column justify-content-center text-center my-2">
                                <p className='Admin__Anecdotes__Text--Small mb-1 text-muted'>#{item.id}</p>
                                <p className='Admin__Anecdotes__Text--Small text-muted'>{formatFullDate(item.createdAtUtc)}</p>
                                <p className="fw-bold fs-6 mb-2 text-white px-2">"{item.content}"</p>
                                <p className="fst-italic text-danger mb-0">- {item.sender || "Anónimo"}</p>
                            </div>

                            {/* Botón Proyectar directo */}
                            <div className="mt-auto">
                                <button
                                    type="button"
                                    className={`Btn__Adsl--${item.isSelected ? 'Success' : 'RedOutlineTransparent'} w-100 mt-2`}
                                    onClick={() => item.isSelected ? requestClearScreen() : requestProject(item)}
                                    disabled={item.isHidden}
                                >
                                    {item.isSelected ? 'Quitar de pantalla' : 'Proyectar ahora'}
                                </button>
                            </div>
                        </div>
                    )) : (
                        <div className="w-100 text-center py-5">
                            <p className="text-muted">No hay anécdotas registradas.</p>
                        </div>
                    )}
                </section>

                {/* BOTÓN CARGAR HISTORIAL ANTERIOR */}
                {nextCursor && (
                    <div className="d-flex justify-content-center mt-4">
                        <button
                            type="button"
                            className="Btn__Adsl--RedOutlineTransparent"
                            onClick={loadMoreHistory}
                            disabled={loading}
                        >
                            {loading ? 'Cargando...' : 'Cargar más anécdotas'}
                        </button>
                    </div>
                )}
            </div>

            {/* BOTÓN BORRAR TODO */}
            <button
                type="button"
                className='Btn__Adsl--RedOutlineTransparent mx-auto mt-5 mb-4 d-block'
                onClick={requestReset}
                title="Borrar todo"
            >
                <i className='bi bi-trash-fill me-1'></i> Borrar todas las anécdotas
            </button>

            {/* MODALES DE CONFIRMACIÓN */}
            {showModal && (
                <>
                    {modalConfig.type === MODAL_TYPES.DELETE && (
                        <Confirm
                            message={`¿Deseas borrar la anécdota #${modalConfig.data?.id}?`}
                            actionToYesButton={executeDelete}
                            textYesButton="Sí, borrar"
                            textNoButton="Cancelar"
                            setShowModal={setShowModal}
                            setConfirmData={() => {}}
                        />
                    )}
                    {modalConfig.type === MODAL_TYPES.RESET && (
                        <Confirm
                            message="¿Deseas borrar TODAS las anécdotas? (Esto no se puede deshacer)"
                            actionToYesButton={executeReset}
                            textYesButton="Sí, borrar todo"
                            textNoButton="Cancelar"
                            setShowModal={setShowModal}
                            setConfirmData={() => {}}
                        />
                    )}
                    {modalConfig.type === MODAL_TYPES.PROJECT && (
                        <Confirm
                            message={`¿Deseas proyectar la anécdota de ${modalConfig.data?.sender || 'Anónimo'} en modo ${displayMode === 'cintillo' ? 'Cintillo' : 'Pantalla Completa'}?`}
                            actionToYesButton={executeProject}
                            textYesButton="Sí, proyectar"
                            textNoButton="Cancelar"
                            setShowModal={setShowModal}
                            setConfirmData={() => {}}
                        />
                    )}
                    {modalConfig.type === MODAL_TYPES.CLEAR_SCREEN && (
                        <Confirm
                            message="¿Deseas quitar la anécdota de la pantalla?"
                            actionToYesButton={executeClearScreen}
                            textYesButton="Sí, quitar"
                            textNoButton="Cancelar"
                            setShowModal={setShowModal}
                            setConfirmData={() => {}}
                        />
                    )}
                    {modalConfig.type === MODAL_TYPES.CLEAR_QUEUE && (
                        <Confirm
                            message="¿Deseas vaciar toda la fila de reproducción?"
                            actionToYesButton={executeClearQueue}
                            textYesButton="Sí, vaciar fila"
                            textNoButton="Cancelar"
                            setShowModal={setShowModal}
                            setConfirmData={() => {}}
                        />
                    )}
                    {modalConfig.type === MODAL_TYPES.TOGGLE_LIVE && (
                        <Confirm
                            message={modalConfig.data?.isGoingLive ? "¿Deseas iniciar la proyección en vivo?" : "¿Deseas detener la proyección en vivo?"}
                            actionToYesButton={executeToggleLive}
                            textYesButton={modalConfig.data?.isGoingLive ? "Sí, iniciar" : "Sí, detener"}
                            textNoButton="Cancelar"
                            setShowModal={setShowModal}
                            setConfirmData={() => {}}
                        />
                    )}
                </>
            )}
        </SecondaryBase>
    );
};

export { Anecdotes };
